import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { login as loginApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Sparkles, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('admin@typingcrm.com');
  const [password, setPassword] = useState('admin123');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await loginApi(email, password);
      // Token stored in httpOnly cookie by backend + in-memory as fallback
      login(res.data.access_token, res.data.user);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background with subtle gradient and pattern */}
      <div 
        className="absolute inset-0 bg-subtle-pattern"
        style={{
          background: `
            radial-gradient(circle at 70% 20%, hsla(188, 64%, 95%, 0.5) 0%, transparent 50%),
            radial-gradient(circle at 20% 80%, hsla(164, 62%, 95%, 0.4) 0%, transparent 40%),
            radial-gradient(circle at 90% 80%, hsla(44, 46%, 96%, 0.3) 0%, transparent 30%),
            hsl(210 25% 98%)
          `
        }}
      />
      
      {/* Floating decorative elements */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-br from-teal-200/20 to-emerald-200/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-72 h-72 bg-gradient-to-br from-amber-200/20 to-orange-200/20 rounded-full blur-3xl" />
      
      <Card className="w-full max-w-md relative z-10 shadow-xl border-slate-200/60 bg-white/90 backdrop-blur-sm">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)] flex items-center justify-center text-white font-bold text-2xl mb-4 shadow-lg shadow-teal-500/25 relative">
            TC
            <Sparkles className="absolute -top-1.5 -right-1.5 w-5 h-5 text-amber-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">Typing Center CRM</CardTitle>
          <CardDescription className="text-slate-500">Sign in to access your dashboard</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@typingcrm.com"
                required
                className="h-11 bg-white border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-11 bg-white border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 btn-primary text-base font-medium gap-2"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </form>
          <div className="mt-6 pt-4 border-t border-slate-100">
            <p className="text-xs text-center text-slate-500">
              Demo credentials: <span className="font-medium text-teal-600">admin@typingcrm.com</span> / <span className="font-medium text-teal-600">admin123</span>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
